/**
 * Paid company holidays — Employee Benefits policy Section 5
 * (federal + Catholic holy days of obligation, one row per calendar date).
 *
 * Ascension: Thursday 40 days after Easter Sunday (Western), computed as
 * Easter Sunday + 39 calendar days (verified for 2026–2027 against USCCB practice).
 *
 * When adding years, extend `WESTERN_EASTER` and append via `holidaysForYear`.
 */

export interface PolicyHolidayRow {
  id: string;
  name: string;
  /** Local calendar date YYYY-MM-DD */
  date: string;
}

/** Human-readable span of calendar years in `POLICY_COMPANY_HOLIDAYS` (UI copy). */
export const POLICY_COMPANY_HOLIDAY_YEARS = '2026–2027';

/** U.S. Thanksgiving — fourth Thursday in November (YYYY-MM-DD). */
export function fourthThursdayNovemberYmd(year: number): string {
  const nov1 = new Date(year, 10, 1);
  const dow = nov1.getDay();
  const firstThu = 1 + ((4 - dow + 7) % 7);
  const fourthThu = firstThu + 21;
  const d = new Date(year, 10, fourthThu);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

const WESTERN_EASTER = {
  /** Verified: Ascension 2026-05-14 (Thursday). */
  2026: [4, 5] as const,
  /** Verified: Ascension 2027-05-06 (Thursday). */
  2027: [3, 28] as const,
} as const;

type PolicyYear = keyof typeof WESTERN_EASTER;

function ascensionYmd(year: PolicyYear): string {
  const [em, ed] = WESTERN_EASTER[year];
  const e = new Date(year, em - 1, ed);
  e.setDate(e.getDate() + 39);
  const m = String(e.getMonth() + 1).padStart(2, '0');
  const day = String(e.getDate()).padStart(2, '0');
  return `${e.getFullYear()}-${m}-${day}`;
}

function holidaysForYear(year: PolicyYear): PolicyHolidayRow[] {
  return [
    {
      id: `${year}-newyears-mary`,
      name: "New Year's Day / Solemnity of Mary, Mother of God",
      date: `${year}-01-01`,
    },
    {
      id: `${year}-ascension`,
      name: 'Ascension of the Lord',
      date: ascensionYmd(year),
    },
    {
      id: `${year}-independence`,
      name: 'Independence Day',
      date: `${year}-07-04`,
    },
    {
      id: `${year}-assumption`,
      name: 'Assumption of the Blessed Virgin Mary',
      date: `${year}-08-15`,
    },
    {
      id: `${year}-all-saints`,
      name: "All Saints' Day",
      date: `${year}-11-01`,
    },
    {
      id: `${year}-thanksgiving`,
      name: 'Thanksgiving Day',
      date: fourthThursdayNovemberYmd(year),
    },
    {
      id: `${year}-immaculate-conception`,
      name: 'Immaculate Conception of the Blessed Virgin Mary',
      date: `${year}-12-08`,
    },
    {
      id: `${year}-christmas`,
      name: 'Christmas Day / Nativity of the Lord (Christmas)',
      date: `${year}-12-25`,
    },
  ];
}

/** All default policy holidays currently maintained (2026–2027). */
export const POLICY_COMPANY_HOLIDAYS: readonly PolicyHolidayRow[] = [...holidaysForYear(2026), ...holidaysForYear(2027)].sort(
  (a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id),
);
